/**
 * TTS via a DOM <audio> element on the media/playback channel (device volume keys).
 * Per-clip RMS loudness normalization (ttsLoudness.js) before playback.
 *
 * unlockAudioPlayback() must run in a click/touch handler so play() works after fetch.
 */

import { prepareTtsForPlayback, warmUpTtsLoudnessEngine } from './ttsLoudness';
import { isIOSDevice, isMobileBrowser } from './micPermission';
import { primeIosAudioAnalysis } from './silenceMonitor';

const TTS_AUDIO_ELEMENT_ID = 'aotf-tts-audio';
/** ~100ms silence — muted play() during a user gesture unlocks autoplay for later TTS clips. */
const SILENT_WAV_DATA_URI =
  'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAAAAAA==';

let sharedHtmlAudio = null;
let htmlAudioPrimed = false;
/** Last navigator.audioSession.type applied — avoid redundant switches (iOS volume HUD jumps). */
let activeAudioSessionType = null;
/** When true, TTS unlock uses play-and-record so mic capture stays available on iOS. */
let micCapturePreferred = false;
/** Defer play-and-record → playback until the current clip ends (avoids mid-clip route blips). */
let deferPlaybackSessionRestore = false;

export function setMicCapturePreferred(preferred) {
  micCapturePreferred = Boolean(preferred);
  if (preferred) {
    deferPlaybackSessionRestore = false;
  }
}
let activePlaybackReject = null;
let activePlayCleanup = null;
let playbackGeneration = 0;
let playSerial = Promise.resolve();

function isPlaybackStale(generation) {
  return generation !== playbackGeneration;
}

export class TtsPlaybackStoppedError extends Error {
  constructor() {
    super('TTS playback stopped');
    this.name = 'TtsPlaybackStoppedError';
  }
}

function settleActivePlayback() {
  activePlaybackReject = null;
}

function rejectActivePlayback() {
  if (activePlaybackReject) {
    const reject = activePlaybackReject;
    settleActivePlayback();
    reject(new TtsPlaybackStoppedError());
  }
}

function setAudioSessionType(type) {
  if (activeAudioSessionType === type) return;
  try {
    if (typeof navigator !== 'undefined' && 'audioSession' in navigator) {
      navigator.audioSession.type = type;
      activeAudioSessionType = type;
    }
  } catch {
    /* ignore */
  }
}

/** iOS: use media volume slider, not ringer. Does not alter gain. */
function ensurePlaybackAudioSession() {
  setAudioSessionType('playback');
}

/** iOS/Safari: mic + TTS need play-and-record; playback-only session blocks capture. */
export function ensureRecordAudioSession() {
  setAudioSessionType('play-and-record');
  primeIosAudioAnalysis();
}

function attachAudioToDocument(audio) {
  if (!audio || audio.isConnected) return;
  try {
    document.body.appendChild(audio);
  } catch {
    /* ignore */
  }
}

function getSharedHtmlAudio() {
  if (typeof window === 'undefined') return null;
  if (!sharedHtmlAudio) {
    const existing = document.getElementById(TTS_AUDIO_ELEMENT_ID);
    if (existing instanceof HTMLAudioElement) {
      sharedHtmlAudio = existing;
    } else {
      sharedHtmlAudio = new Audio();
      sharedHtmlAudio.id = TTS_AUDIO_ELEMENT_ID;
      sharedHtmlAudio.setAttribute('playsinline', 'true');
      sharedHtmlAudio.setAttribute('webkit-playsinline', 'true');
      sharedHtmlAudio.preload = 'auto';
      attachAudioToDocument(sharedHtmlAudio);
    }
  }
  sharedHtmlAudio.muted = false;
  return sharedHtmlAudio;
}

function runActivePlayCleanup() {
  if (activePlayCleanup) {
    const cleanup = activePlayCleanup;
    activePlayCleanup = null;
    cleanup();
  }
}

/** Between consecutive TTS clips — keep the media session alive on mobile (no src=''/load()). */
function resetBetweenClips() {
  runActivePlayCleanup();
  const a = sharedHtmlAudio;
  if (!a) return;
  try {
    a.pause();
    a.currentTime = 0;
  } catch {
    /* ignore */
  }
  a.muted = false;
  if (!isMobileBrowser()) {
    a.src = '';
  }
}

function haltCurrentClip() {
  runActivePlayCleanup();
  const a = sharedHtmlAudio;
  if (!a) return;
  try {
    a.pause();
    a.currentTime = 0;
  } catch {
    /* ignore */
  }
  a.muted = false;
  try {
    a.removeAttribute('src');
    a.load();
  } catch {
    /* ignore */
  }
}

/**
 * Muted silent play during a user gesture satisfies autoplay policy before async TTS fetch.
 * Real MP3 is not primed here — that caused iOS AGC drift on the first spoken clip.
 */
function primeHtmlAudio() {
  if (htmlAudioPrimed) return;
  const audio = getSharedHtmlAudio();
  if (!audio) return;
  const mobile = isMobileBrowser();
  try {
    audio.muted = true;
    if (!mobile) {
      audio.volume = 0;
    }
    audio.src = SILENT_WAV_DATA_URI;
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => {
          try {
            audio.pause();
            audio.currentTime = 0;
          } catch {
            /* ignore */
          }
          audio.muted = false;
          if (!mobile) {
            audio.volume = 1;
          }
          if (!mobile) {
            audio.src = '';
          }
          htmlAudioPrimed = true;
        })
        .catch(() => {
          htmlAudioPrimed = false;
        });
      return;
    }
    htmlAudioPrimed = true;
  } catch {
    htmlAudioPrimed = false;
  }
}

/**
 * @param {{ force?: boolean }} [options]
 */
function ensureOutputAudioSession() {
  if (micCapturePreferred) {
    ensureRecordAudioSession();
    return;
  }
  ensurePlaybackAudioSession();
}

function isTtsActivelyPlaying() {
  return activePlaybackReject != null;
}

export function unlockAudioPlayback(options = {}) {
  if (typeof window === 'undefined') return;
  // Session switches (playback ↔ play-and-record) must not reset the silent prime —
  // re-priming retriggers iOS route-change sounds and AGC drift on the next clip.
  void options;
  ensureOutputAudioSession();
  primeHtmlAudio();
  void warmUpTtsLoudnessEngine();
}

function maybeCompleteDeferredPlaybackRestore() {
  if (!deferPlaybackSessionRestore || micCapturePreferred || isTtsActivelyPlaying()) {
    return;
  }
  deferPlaybackSessionRestore = false;
  ensurePlaybackAudioSession();
}

/** After mic capture, restore media playback routing when voice output stays on. */
export function restorePlaybackAudioSession() {
  if (micCapturePreferred) return;
  if (isTtsActivelyPlaying()) {
    deferPlaybackSessionRestore = true;
    return;
  }
  deferPlaybackSessionRestore = false;
  ensurePlaybackAudioSession();
}

export function isAutoplayBlockedError(e) {
  if (!e || typeof e !== 'object') return false;
  if (e.name === 'NotAllowedError') return true;
  const msg = String(e.message || '');
  return (
    /not allowed by the user agent/i.test(msg) ||
    /user denied permission/i.test(msg)
  );
}

/**
 * @param {{ forMicCapture?: boolean }} [options]
 * forMicCapture: softer halt on iOS so the route can hand off to SpeechRecognition.
 */
export function stopActiveTtsPlayback(options = {}) {
  playbackGeneration += 1;
  rejectActivePlayback();
  if (options.forMicCapture === true && isIOSDevice()) {
    resetBetweenClips();
  } else {
    haltCurrentClip();
  }
  maybeCompleteDeferredPlaybackRestore();
}

/**
 * @returns {Promise<{ kind: 'html', objectUrl: string | null, audio: HTMLAudioElement | null }>}
 */
export function playTtsBlob(blob) {
  const next = playSerial.then(() => playTtsBlobOnce(blob));
  playSerial = next.catch(() => {});
  return next;
}

async function playTtsBlobOnce(blob) {
  if (typeof window === 'undefined' || !blob) {
    throw new Error('No audio blob');
  }

  if (micCapturePreferred) {
    ensureRecordAudioSession();
  } else {
    maybeCompleteDeferredPlaybackRestore();
    ensurePlaybackAudioSession();
  }

  playbackGeneration += 1;
  rejectActivePlayback();
  resetBetweenClips();
  const generation = playbackGeneration;

  return new Promise((resolve, reject) => {
    activePlaybackReject = reject;

    const finish = (result) => {
      if (isPlaybackStale(generation)) {
        reject(new TtsPlaybackStoppedError());
        return;
      }
      if (activePlayCleanup) {
        activePlayCleanup = null;
      }
      settleActivePlayback();
      maybeCompleteDeferredPlaybackRestore();
      resolve(result);
    };
    const fail = (err) => {
      if (activePlayCleanup) {
        activePlayCleanup = null;
      }
      settleActivePlayback();
      reject(err);
    };

    void (async () => {
      const audio = getSharedHtmlAudio();
      if (!audio) {
        fail(new Error('Audio not supported'));
        return;
      }
      if (isPlaybackStale(generation)) {
        fail(new TtsPlaybackStoppedError());
        return;
      }

      const { blob: playbackBlob, elementVolume } = await prepareTtsForPlayback(blob);
      if (isPlaybackStale(generation)) {
        fail(new TtsPlaybackStoppedError());
        return;
      }

      audio.muted = false;
      if (!isMobileBrowser()) {
        audio.volume = elementVolume;
      }
      const url = URL.createObjectURL(playbackBlob);
      audio.src = url;
      try {
        await audio.play();
      } catch (e) {
        URL.revokeObjectURL(url);
        audio.src = '';
        fail(e);
        return;
      }
      if (isPlaybackStale(generation)) {
        URL.revokeObjectURL(url);
        resetBetweenClips();
        fail(new TtsPlaybackStoppedError());
        return;
      }
      const onEnded = () => {
        cleanup();
        finish({ kind: 'html', objectUrl: url, audio });
      };
      const onError = () => {
        cleanup();
        fail(new Error('HTMLAudioElement playback error'));
      };
      const cleanup = () => {
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('error', onError);
        URL.revokeObjectURL(url);
      };
      activePlayCleanup = cleanup;
      audio.addEventListener('ended', onEnded, { once: true });
      audio.addEventListener('error', onError, { once: true });
    })();
  });
}
